/**
 * 离线词典 — AI 不可用时的兜底释义
 *
 * MVP 方案：内置精简常用词词典（~200 词），覆盖最常见场景。
 * 完整 ECDICT（SQLite，几十 MB）在 v1.1 随 App 打包。
 *
 * 对齐 features.md 9.14：离线词典兜底
 * 对齐 architecture.md §七：AI 不可用时回退离线词典
 */

export interface DictEntry {
  word: string;
  phonetic?: string;
  pos?: string;
  definitions: string[];
}

import { DICT_DATA } from './data';

/**
 * 内置词典（CET-4/6 高频 ~450 词）
 * 完整 ECDICT（数万词）在 v1.1 随 App 打包
 */
const BUILT_IN_DICT: DictEntry[] = DICT_DATA;

/** 构建查找索引 */
const dictMap = new Map<string, DictEntry>();
for (const entry of BUILT_IN_DICT) {
  dictMap.set(entry.word.toLowerCase(), entry);
}

/**
 * 查询单词释义（离线）
 * @param word 要查的词（不区分大小写）
 * @returns 词典条目，未找到返回 null
 */
export function lookupWord(word: string): DictEntry | null {
  const normalized = word.toLowerCase().trim();
  if (!normalized) return null;

  // 精确匹配
  const exact = dictMap.get(normalized);
  if (exact) return exact;

  // 去除常见后缀的变形匹配
  const stems = [
    normalized.replace(/(s|es|ed|ing|er|est|ly|tion|ment|ness)$/, ''),
    normalized.replace(/(ies)$/, 'y'),
    normalized.replace(/(ied|ies)$/, 'y'),
  ];
  for (const stem of stems) {
    if (stem && stem !== normalized) {
      const found = dictMap.get(stem);
      if (found) return found;
    }
  }

  return null;
}

/**
 * 翻译查询：优先 AI，兜底离线词典
 *
 * 对齐 api-unified.md §二 AiProviderFactory 选择逻辑：
 *   有 Key → AI 精译
 *   无 Key → 离线词典基础释义
 */
export async function translateWordOrPhrase(
  text: string,
  opts?: { aiConfigured: boolean }
): Promise<{ source: 'ai' | 'offline' | 'none'; result: string; entry?: DictEntry }> {
  // 单词 → 尝试离线词典（无论 AI 是否可用，单词都先查离线补充音标/词性）
  const words = text.trim().split(/\s+/);
  const isSingleWord = words.length === 1;

  // 短语/句子 → 只有 AI 能翻译
  if (isSingleWord) {
    const entry = lookupWord(text);
    if (entry) {
      // 有 AI 时优先返回 AI 结果，离线作为补充数据（音标、词性）
      // MVP 简化：离线词典命中就返回（省 API 调用）
      return {
        source: 'offline',
        result: `${entry.phonetic ? entry.phonetic + ' ' : ''}${entry.pos ? entry.pos + ' ' : ''}${entry.definitions.join('；')}`,
        entry,
      };
    }
  }

  // 离线词典未命中 → AI
  if (opts?.aiConfigured) {
    try {
      const { chatCompletion } = await import('../ai/provider');
      const result = await chatCompletion(
        [
          { role: 'system', content: '将以下文本翻译为中文，只输出译文：' },
          { role: 'user', content: text },
        ],
        { temperature: 0.3 }
      );
      return { source: 'ai', result: result.trim() };
    } catch {
      return { source: 'none', result: '翻译失败，请重试' };
    }
  }

  return {
    source: 'none',
    result: isSingleWord
      ? '离线词典未收录此词，请配置 AI 获取完整翻译'
      : '请配置 AI API Key 以翻译句子',
  };
}
